<script setup lang="ts">
  import {
    Calendar,
    CalendarSize,
    Floating,
    FloatingPlacement,
    type DateISO,
    type CalendarDateRange,
  } from '@travelata-libs/ui-kit';
  import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
  import DateFromFlexChips from './DateFromFlexChips.vue';

  const VIEWPORT_BOTTOM_GAP = 16;
  const MAX_SETTLE_FRAMES = 30;

  interface DateFromDropdownProps {
    anchorIso: string;
    flexibleForCalendar?: number | null;
    flexible: number;
    minDate: string;
    maxDate: string;
    visible: boolean;
  }

  const props = withDefaults(defineProps<DateFromDropdownProps>(), {
    flexibleForCalendar: null,
  });

  interface DateFromDropdownEmits {
    (e: 'date-click', iso: string): void;
    (e: 'select-flexible', n: number): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<DateFromDropdownEmits>();

  const dropdownRef = ref<HTMLElement | null>(null);
  let settleRaf = 0;

  const cancelSettle = (): void => {
    if (settleRaf) {
      window.cancelAnimationFrame(settleRaf);
      settleRaf = 0;
    }
  };

  const scrollBottomIntoView = (): void => {
    const el = dropdownRef.value;
    if (!el) {
      return;
    }
    const overflow = Math.ceil(el.getBoundingClientRect().bottom + VIEWPORT_BOTTOM_GAP - window.innerHeight);
    if (overflow > 0) {
      window.scrollBy({ top: overflow, behavior: 'smooth' });
    }
  };

  const scheduleScrollBottomIntoView = (): void => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      return;
    }
    cancelSettle();
    let previousBottom = Number.NaN;
    let frames = 0;
    const waitForSettle = (): void => {
      settleRaf = 0;
      const el = dropdownRef.value;
      if (!props.visible || !el) {
        return;
      }
      const bottom = el.getBoundingClientRect().bottom;
      if (bottom === previousBottom || frames >= MAX_SETTLE_FRAMES) {
        scrollBottomIntoView();
        return;
      }
      previousBottom = bottom;
      frames += 1;
      settleRaf = window.requestAnimationFrame(waitForSettle);
    };
    settleRaf = window.requestAnimationFrame(waitForSettle);
  };

  const onDateClick = (payload: DateISO | CalendarDateRange<DateISO> | null): void => {
    if (typeof payload === 'string') {
      emit('date-click', payload);
    } else if (payload?.start) {
      emit('date-click', payload.start);
    }
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      emit('close');
    }
  };

  const onSelectFlexible = (n: number) => {
    emit('select-flexible', n);
  };

  const onHide = (): void => {
    if (props.visible) {
      emit('close');
    }
  };

  watch(
    () => props.visible,
    (visible) => {
      if (visible) {
        document.addEventListener('keydown', onKeydown);
        nextTick(scheduleScrollBottomIntoView);
      } else {
        document.removeEventListener('keydown', onKeydown);
        cancelSettle();
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
    cancelSettle();
  });
</script>

<template>
  <Floating
    :visible="visible"
    :placement="FloatingPlacement.Bottom"
    :disable-flip="true"
    @hide="onHide"
  >
    <template #reference>
      <slot name="reference" />
    </template>

    <template #floating>
      <div
        ref="dropdownRef"
        class="date-from-dropdown"
        data-testid="date-from-dropdown"
      >
        <Calendar
          data-testid="date-from-calendar"
          :model-value="anchorIso"
          double
          :size="CalendarSize.M"
          :range="false"
          :flexible-days="flexibleForCalendar"
          :with-footer="false"
          :min-date="minDate"
          :max-date="maxDate"
          @date-click="onDateClick"
        />

        <DateFromFlexChips
          :value="flexible"
          @select="onSelectFlexible"
        />
      </div>
    </template>
  </Floating>
</template>

<style scoped lang="scss">
  .date-from-dropdown {
    background: var(--color-bg-primary);
    border-radius: var(--rounding-s);
    box-shadow: var(--shadow-m);
    display: flex;
    flex-direction: column;
    padding: var(--spacing-l);
    width: max-content;
  }
</style>
